// Flow to sales: the diagram shows where stock flows; this section
// quantifies the ratio and, per location, how that outflow divides across
// sales channels. Sorted ascending by cover so the tightest location leads.
//
// Month mode shows outflow against stock as a TURNS MULTIPLE, not a
// percentage: a location routinely ships more than its own on-hand stock in
// a month, and "161% of stock" is not a thing anyone can act on.
import {
  SnapshotChannel,
  SnapshotLocation,
  SnapshotPeriod,
  SnapshotSales,
} from "./types";
import {
  CHANNEL_RAMP,
  DeltaChip,
  EM_DASH,
  RoleDot,
  SnapCard,
  un,
  usd,
  useSnapshotTokens,
} from "./primitives";

const ChannelLegend = ({ channels }: { channels: SnapshotChannel[] }) => {
  const t = useSnapshotTokens();
  return (
    <div
      style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}
    >
      {channels.map((c, i) => (
        <span
          key={c.channel}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontSize: 10.5,
            color: t.ink3,
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: c.isOther ? t.ink4 : CHANNEL_RAMP[i % CHANNEL_RAMP.length],
            }}
          />
          {c.channel}
        </span>
      ))}
    </div>
  );
};

export const FlowToSales = ({
  locations,
  sales,
  channels,
  period,
}: {
  locations: SnapshotLocation[];
  sales: SnapshotSales;
  channels: SnapshotChannel[];
  period: SnapshotPeriod;
}) => {
  const t = useSnapshotTokens();
  const isMonth = period === "month";
  // "/mo" would read as a monthly rate; the month figure is a to-date total.
  const per = isMonth ? " MTD" : "/wk";
  const coverUnit = isMonth ? "mo" : "w";
  const channelIndex = new Map(channels.map((c, i) => [c.channel, i]));

  const rows = locations
    .map((l) => {
      const share =
        l.weeklyOut != null && l.units != null && l.units > 0
          ? l.weeklyOut / l.units
          : null;
      const cover =
        l.weeklyOut != null && l.weeklyOut > 0 && l.units != null
          ? l.units / l.weeklyOut
          : null;
      return { ...l, share, cover };
    })
    .sort((a, b) => (a.cover ?? Infinity) - (b.cover ?? Infinity));
  const maxShare = Math.max(1e-9, ...rows.map((r) => r.share ?? 0));

  return (
    <SnapCard pad={16} style={{ display: "flex", gap: 22, alignItems: "stretch" }}>
      <div
        style={{
          minWidth: 168,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          paddingRight: 22,
          borderRight: `1px solid ${t.line}`,
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 600, color: t.ink3 }}>
          Shipped to customers
        </span>
        <span
          style={{
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: -0.8,
            lineHeight: 1.05,
            color: sales.weeklyUnits == null ? t.ink4 : t.ink,
          }}
          title={
            sales.weeklyUnits == null
              ? "No shipment figure available for this run"
              : isMonth
              ? "Actuals for the complete weeks of this month so far"
              : "Average over the 4 complete weeks before the run's data date"
          }
        >
          {sales.weeklyUnits == null ? EM_DASH : un(sales.weeklyUnits)}
          <span style={{ fontSize: 13, fontWeight: 500, color: t.ink3 }}>
            {" u"}
            {per}
          </span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <DeltaChip
            current={sales.weeklyUnits}
            prev={sales.weeklyUnitsPrev}
            unit="u"
            good="up"
          />
          {sales.weeklyUnitsPrev != null && (
            <span style={{ fontSize: 10.5, color: t.ink4 }}>vs last run</span>
          )}
        </div>
        <span
          style={{ fontSize: 12.5, color: t.ink2, marginTop: 6, fontWeight: 600 }}
        >
          {sales.weeklyRevenue == null ? EM_DASH : usd(sales.weeklyRevenue, 2)}
          <span style={{ fontWeight: 400, color: t.ink4 }}>
            {" revenue"}
            {per}
          </span>
        </span>
        <span
          style={{
            fontSize: 11.5,
            color: t.greenInk,
            fontWeight: 600,
            marginTop: 4,
          }}
          title="Σ outflow ÷ Σ stock across the locations listed here"
        >
          {sales.turnRate == null
            ? EM_DASH
            : isMonth
            ? `${sales.turnRate.toFixed(2)}× of stock turns monthly`
            : `${(sales.turnRate * 100).toFixed(1)}% of stock turns weekly`}
        </span>
        {sales.turnRatePrev != null && (
          <span style={{ fontSize: 10.5, color: t.ink4 }}>
            was{" "}
            {isMonth
              ? `${sales.turnRatePrev.toFixed(2)}×`
              : `${(sales.turnRatePrev * 100).toFixed(1)}%`}{" "}
            last run
          </span>
        )}
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 9,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 10.5, color: t.ink4 }}>
            Outflow against stock on hand
          </span>
          <div style={{ flex: 1 }} />
          {channels.length > 0 && <ChannelLegend channels={channels} />}
        </div>
        {rows.map((l) => (
          <div
            key={l.id}
            style={{
              display: "grid",
              gridTemplateColumns:
                "128px minmax(0, 1fr) 72px 88px 96px 132px",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                fontSize: 12,
                fontWeight: 600,
                color: t.ink,
                minWidth: 0,
              }}
            >
              <RoleDot role={l.role} />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {l.name}
              </span>
            </span>
            <div
              style={{
                position: "relative",
                height: 12,
                background: t.line2,
                borderRadius: 3,
                overflow: "hidden",
              }}
              title={
                l.share == null
                  ? `No outflow data for ${l.name}`
                  : `${un(l.weeklyOut as number)} of ${un(
                      l.units as number
                    )} units leave per ${isMonth ? "month" : "week"}`
              }
            >
              {l.share != null && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: Math.min(100, (l.share / maxShare) * 100) + "%",
                    background: t.greenInk,
                    opacity: 0.55,
                    borderRadius: 3,
                  }}
                />
              )}
            </div>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: l.share == null ? t.ink4 : t.greenInk,
                textAlign: "right",
              }}
            >
              {l.share == null
                ? EM_DASH
                : isMonth
                ? `${l.share.toFixed(2)}×`
                : `${(l.share * 100).toFixed(1)}%`}
            </span>
            <span style={{ fontSize: 11.5, color: t.ink3, textAlign: "right" }}>
              {l.weeklyOut == null ? EM_DASH : `${un(l.weeklyOut)} u${per}`}
            </span>
            <span style={{ fontSize: 11.5, color: t.ink4, textAlign: "right" }}>
              {l.cover == null
                ? EM_DASH
                : `${l.cover.toFixed(1)}${coverUnit} of cover`}
            </span>
            {/* Channel mix: how this location's outflow divides. A DC that
                is mostly one channel makes its stock-out that channel's
                problem specifically. */}
            <div
              style={{
                display: "flex",
                height: 12,
                borderRadius: 3,
                overflow: "hidden",
                background: t.line2,
              }}
            >
              {l.channelMix?.map((m) => (
                <div
                  key={m.channel}
                  title={`${m.channel}: ${m.units.toLocaleString(
                    "en-US"
                  )} u (${Math.round(m.share * 100)}%)`}
                  style={{
                    width: m.share * 100 + "%",
                    background:
                      m.channel === channels.find((c) => c.isOther)?.channel
                        ? t.ink4
                        : CHANNEL_RAMP[
                            (channelIndex.get(m.channel) ?? 0) %
                              CHANNEL_RAMP.length
                          ],
                    opacity: 0.85,
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </SnapCard>
  );
};
